import type {
  Attribute,
  BrandBase,
  Category,
  CheckoutPaymentMode,
  Image,
  Label,
  Price,
  Product,
  ProductType,
  Stock,
  Store,
} from '@maestro/data-layer'

export interface CartBase {
  cartId: string
  cartVersion: number
  customerId?: string
  ttl: number
  userCartGuid?: string
  totalQuantity: number
}

export interface Cart extends CartBase {
  entries: CartEntry[]
  couponCodes: CouponCode[]
  priceSummary: PriceSummary
  freeShippingBar: FreeShippingBar
  deliveryPointOfService?: Store
  deliveryAddress?: CartAddress
  orgUnit?: CartOrgUnit
  orderDetails?: CartOrderDetails
  deliveryMode?: CartDeliveryMode
  billingAddress?: CartAddress
  paymentMode?: CheckoutPaymentMode
}

export interface CartLight extends CartBase {
  priceSummary: PriceSummary
}

export interface CartEntry {
  id: string
  urlPath: string // without leading slash
  image: Image
  productId: string
  variantId: string
  sku?: string
  productName: string
  brand?: BrandBase
  quantity: number
  priceBase: Price
  price: Price
  labels: Label[]
  attributes: Attribute[]
  recommendation?: CartEntryRecommendation
  stock: Stock[]
  properties?: Partial<Record<CartEntryPropertyKey, string | number | boolean>>
  productType: ProductType
  type?: string
  categories?: Category[]
  packagingUnit?: string
}

export interface CartEntryRecommendation {
  title?: string
  description?: string
  products: Product[]
}

export interface CouponCode {
  id: string
  code: string
  label?: string
  description?: string
}

export interface PriceSummary {
  grandTotal: Price
  subTotal: Price
  totalTax: Price
  totalDiscounts: Price
  deliveryCost?: Price
}

export interface FreeShippingBar {
  amountLeft?: Price
  amountThreshold?: Price
  deliveryFrom?: Price
  progress: number
}

export interface CartAddress {
  id: string
  key?: string
  title?: string
  salutation?: string
  firstName?: string
  lastName?: string
  streetName?: string
  streetNumber?: string
  additionalStreetInfo?: string
  postalCode?: string
  city?: string
  region?: string
  state?: string
  countryIso: string
  countryName: string
  company?: string
  department?: string
  building?: string
  apartment?: string
  pOBox?: string
  phone?: string
  mobile?: string
  email?: string
  fax?: string
  additionalAddressInfo?: string
  externalId?: string
}

export interface CartRecord {
  cartEntryId?: string
  productId: string
  variantId: string
  quantity: number
}

export interface CartEntryStatus {
  productId: string
  status: string
}

export interface CartOrgUnit {
  name?: string
  uid: string
  active?: boolean
}

export interface CartOrderDetails {
  purchaseOrderNumber?: string
  orderConfirmationEmail?: string
  specialRequests?: string
  requestedDeliveryDate?: string
  contactName?: string
  contactPhone?: string
  isSingleShipmentRequested?: boolean
  isFutureDeliveryRequested?: boolean
}

export interface CartDeliveryMode {
  deliveryCost?: Price
  name?: string
}

export interface ImportProductsToCartSuccess {
  importedCount: number
  invalidLines: number[]
  unknownItemNumbers: string[]
  productIdItemNumbers: string[]
  zeroQuantityItemNumbers: string[]
}

export interface CartProduct extends Pick<
  Product,
  | 'id'
  | 'urlPath'
  | 'name'
  | 'brand'
  | 'image'
  | 'price'
  | 'gallery'
  | 'variantAttributes'
  | 'variants'
  | 'productType'
> {
  defaultVariantId: string
}

export enum CartEntryPropertyKey {}
